import { oc } from '@orpc/contract';
import * as z from 'zod';
import { playbackGateReasonSchema } from '../enums/playback-gate.js';
import { childIdSchema, contentItemIdSchema } from '../primitives/id.js';
import { instantSchema } from '../primitives/time.js';
import { API_VERSION } from './version.js';
/**
 * What a child watched, told to the parent who chose it.
 *
 * Two sources, kept apart on purpose. **Viewing sessions** have existed since
 * the budget did and answer "how long, when, and which video": that history is
 * already there. **Views** are new: the tablet reports the stretches of a video
 * a child actually watched, which is what answers "where did she rewind, where
 * did she leave, how did it end". A view exists only from the tablet build that
 * reports them, and the screens say so rather than showing zeros as if they
 * were findings.
 *
 * Kept indefinitely: the owner's choice on the board in
 * `.docs/design/watch-analytics` (2026-10-09).
 */
const analytics = `/${API_VERSION}/analytics`;
/** Seconds into the video. */
const positionSchema = z
    .number()
    .nonnegative()
    .max(24 * 60 * 60);
/**
 * One stretch watched without a jump, `[from, to]` in seconds.
 *
 * A seek ends one stretch and starts the next, so the gaps and overlaps
 * between consecutive stretches are the seeks: forward where the next starts
 * later, back where it starts earlier and the child watched something again.
 */
export const watchedRangeSchema = z.tuple([positionSchema, positionSchema]);
/**
 * How a viewing ended.
 *
 * - `COMPLETED` — it reached the end.
 * - `LEFT` — the child went back or chose something else.
 * - `STOPPED` — the gate ended it; `gateReason` says why (the budget, a rest,
 *   a parent's pause).
 * - `BACKGROUND` — the app left the screen.
 * - `UNAVAILABLE` — the video could not be played.
 */
export const viewEndReasonSchema = z.enum([
    'COMPLETED',
    'LEFT',
    'STOPPED',
    'BACKGROUND',
    'UNAVAILABLE',
]);
export const viewEndSchema = z.object({
    at: instantSchema,
    reason: viewEndReasonSchema,
    gateReason: playbackGateReasonSchema.optional(),
});
/** The ceiling on stretches in one viewing. A child who seeks more than this is drumming. */
export const MAX_RANGES_PER_VIEW = 400;
/**
 * One viewing of one video, as the tablet reports it.
 *
 * Sent while it plays and once more when it ends, always whole: the latest
 * report replaces the earlier ones. `viewId` is the tablet's own, so a report
 * retried after a lost response is the same viewing and not a second one.
 */
export const viewReportSchema = z.object({
    viewId: z.uuid(),
    contentItemId: contentItemIdSchema,
    openedAt: instantSchema,
    ranges: z.array(watchedRangeSchema).max(MAX_RANGES_PER_VIEW),
    durationSeconds: positionSchema.nullable(),
    end: viewEndSchema.optional(),
});
export const ANALYTICS_PERIODS = [1, 7, 30];
export const analyticsPeriodSchema = z.union([z.literal(1), z.literal(7), z.literal(30)]);
/** Opened and closed again within this, it was not what she wanted. */
export const QUICK_QUIT_SECONDS = 60;
const itemStatSchema = z.object({
    contentItemId: contentItemIdSchema,
    opens: z.int().nonnegative(),
    watchedSeconds: z.int().nonnegative(),
    /** Mean share of the video watched per viewing, 0–1. Null without views. */
    averageCompletion: z.number().min(0).max(1).nullable(),
});
/**
 * How viewings ended, grouped the way a parent thinks about them: the limit and
 * the schedule are "time was up", a rest and a movement break are "a break".
 */
export const endingKindSchema = z.enum(['COMPLETED', 'LEFT', 'TIME_UP', 'BREAK', 'PARENT']);
export const watchingOverviewSchema = z.object({
    /** The family's local days the period covers, oldest first. */
    days: z.array(z.object({
        day: z.iso.date(),
        watchedSeconds: z.int().nonnegative(),
        opens: z.int().nonnegative(),
    })),
    /** Seconds watched per local day and hour; hours with nothing are left out. */
    hours: z.array(z.object({
        day: z.iso.date(),
        hour: z.int().min(0).max(23),
        watchedSeconds: z.int().nonnegative(),
    })),
    dailyLimitSeconds: z.int().nonnegative(),
    totals: z.object({
        watchedSeconds: z.int().nonnegative(),
        opens: z.int().nonnegative(),
        distinctItems: z.int().nonnegative(),
        quickQuits: z.int().nonnegative(),
        /** Share of reported viewings that reached the end. Null without views. */
        completionRate: z.number().min(0).max(1).nullable(),
    }),
    /** Most opened first. */
    top: z.array(itemStatSchema),
    /** On her tablet in this period and never opened. Oldest approval first. */
    neverWatched: z.array(z.object({ contentItemId: contentItemIdSchema, approvedAt: instantSchema })),
    /** Opened at least twice, and left within `QUICK_QUIT_SECONDS` more often than not. */
    quickQuits: z.array(z.object({
        contentItemId: contentItemIdSchema,
        opens: z.int().positive(),
        quickQuits: z.int().positive(),
    })),
    /** Empty without views. */
    endings: z.array(z.object({ kind: endingKindSchema, count: z.int().positive() })),
    /** Whether this child's tablet reports views at all yet. */
    hasViews: z.boolean(),
});
export const watchedViewSchema = z.object({
    viewId: z.uuid(),
    openedAt: instantSchema,
    ranges: z.array(watchedRangeSchema),
    end: viewEndSchema.nullable(),
});
export const watchingItemSchema = z.object({
    contentItemId: contentItemIdSchema,
    opens: z.int().nonnegative(),
    watchedSeconds: z.int().nonnegative(),
    /** Where the next opening resumes. */
    resumeSeconds: z.int().nonnegative(),
    /** Newest first. Empty until the tablet reports views. */
    views: z.array(watchedViewSchema),
    hasViews: z.boolean(),
});
/** A query string carries "7", not 7. */
const periodQuerySchema = z.coerce.number().pipe(analyticsPeriodSchema);
const periodInput = { childId: childIdSchema, days: periodQuerySchema };
export const analyticsContract = {
    /** Device-authenticated: the tablet telling the server what was watched. */
    reportView: oc
        .route({ method: 'POST', path: `${analytics}/views` })
        .input(viewReportSchema)
        .output(z.object({ success: z.literal(true) }))
        .errors({
        CONTENT_NOT_FOUND: { status: 404, message: 'Content not found' },
        DEVICE_UNPAIRED: { status: 403, message: 'This device is no longer paired' },
    }),
    overview: oc
        .route({ method: 'GET', path: `${analytics}/children/{childId}` })
        .input(z.object(periodInput))
        .output(watchingOverviewSchema)
        .errors({ CHILD_NOT_FOUND: { status: 404, message: 'Child not found' } }),
    item: oc
        .route({ method: 'GET', path: `${analytics}/children/{childId}/items/{contentItemId}` })
        .input(z.object({ ...periodInput, contentItemId: contentItemIdSchema }))
        .output(watchingItemSchema)
        .errors({
        CHILD_NOT_FOUND: { status: 404, message: 'Child not found' },
        CONTENT_NOT_FOUND: { status: 404, message: 'Content not found' },
    }),
    /**
     * How often each item was opened, by any child, in the last `days`.
     *
     * For the collection page, which is the family's and not one child's.
     */
    itemOpens: oc
        .route({ method: 'GET', path: `${analytics}/item-opens` })
        .input(z.object({ days: periodQuerySchema }))
        .output(z.object({
        items: z.array(z.object({ contentItemId: contentItemIdSchema, opens: z.int().positive() })),
    })),
};
//# sourceMappingURL=analytics.js.map